// The support ticket LISTS (Support and System) and the new-ticket
// dialog; the conversation view lives in the web-ticket-view component.
// Merged onto the web-dashboard prototype (see dashboard.js);
// `this` is the component instance.
export default {

    // Ticket-list "Last reply" column. `adminView` is the System (admin) table,
    // where the actual person is named; the user's own Support list shows only
    // "You" / "Support".
    lastReplyBy(t, adminView) {
      if (t.last_reply_is_admin) return adminView ? (t.last_reply_name || 'Support') : 'Support';
      return adminView ? (t.last_reply_name || 'Customer') : 'You';
    },

    lastReplyStatus(t) {
      if (!t.last_reply_at) return '';
      return t.last_reply_read ? 'Read · ' + this.formatDateTime(t.last_reply_read_at) : 'Not read yet';
    },

    // Merged Status column for the ticket lists: a closed ticket carries the
    // customer's verdict once given — Open, Closed, Closed / Solved,
    // Closed / Unsolved. Only an unsolved closure demands attention (red);
    // every settled closure is muted gray, verdict or not.
    ticketStatusLabel(t) {
      if (t.status !== 'closed' || t.solved == null) return this.formatStatus(t.status);
      return t.solved ? 'Closed / Solved' : 'Closed / Unsolved';
    },

    ticketStatusVariant(t) {
      if (t.status !== 'closed' || t.solved == null) return this.statusVariant(t.status);
      return t.solved ? 'secondary' : 'danger';
    },

    async loadTickets() {
      // A customer's list is their history: closed tickets shown by default.
      this.tableState('tickets').hideClosed ??= false;
      try {
        if (await this.fetchList('tickets', `${this.apiUrl('tickets')}`)) {
          this.ticketsLoaded = true;
        }
      } catch (e) {}
    },

    async loadTicketUnreadCounts() {
      try {
        const res = await this.apiFetch(`${this.apiUrl('tickets/unread')}`);
        if (res.ok) {
          const data = await res.json();
          this.ticketUnreadCount = data.count || 0;
        }
      } catch (e) {}
      if (this.isAdmin) {
        try {
          const res = await this.apiFetch(`${this.apiUrl('admin/tickets/unread')}`);
          if (res.ok) {
            const data = await res.json();
            this.adminTicketUnreadCount = data.count || 0;
          }
        } catch (e) {}
      }
    },

    async loadAdminTickets() {
      // The System list is a work queue: settled tickets hidden by default.
      this.tableState('adminTickets').hideClosed ??= true;
      try {
        if (await this.fetchList('adminTickets', `${this.apiUrl('admin/tickets')}`)) {
          this.adminTicketsLoaded = true;
        }
      } catch (e) {}
    },

    // Open a ticket from a list: record the view source and push the ticket
    // route (a history entry, so Back returns to the list). applyRoute then
    // renders it via viewTicket.
    // Show/hide closed tickets. The choice rides the table's URL state, so
    // refresh and Back keep it.
    toggleClosedTickets(name) {
      const st = this.tableState(name);
      st.hideClosed = !st.hideClosed;
      st.page = 1;
      this._refetch(name);
      this._syncUrl();
    },

    openTicket(ticketID, source) {
      this.ticketViewSource = source;
      // Admins can view their own tickets from either side; the sided path
      // carries which one, so refresh and shared links keep the perspective
      // (and the two views get distinct URLs).
      this._go((source === 'admin' ? 'system' : 'support') + '/tickets/' + ticketID);
    },

    openNewTicketDialog() {
      this.newTicketSubject = '';
      this.newTicketBody = '';
      this._revokePicked(this.newTicketFiles);
      this.newTicketFiles = [];
      this.update();
      this.querySelector('.new-ticket-dialog').showModal();
    },

    closeNewTicketDialog() {
      this.querySelector('.new-ticket-dialog').close();
    },

    async submitNewTicket() {
      if (this.newTicketLoading) return;
      if (!this.newTicketSubject || (!this.newTicketBody && this.newTicketFiles.length === 0)) {
        this.showToast('Subject and a message or attachment are required', 'danger');
        return;
      }
      this.newTicketLoading = true;
      this.update();
      try {
        // Attachments switch the post to multipart/form-data. The browser
        // sets the Content-Type (with boundary) for FormData bodies; apiFetch
        // only adds the Authorization header.
        let options;
        if (this.newTicketFiles.length > 0) {
          const fd = new FormData();
          fd.append('subject', this.newTicketSubject);
          fd.append('body', this.newTicketBody);
          for (const f of this.newTicketFiles) fd.append('files', f, f.name);
          options = { method: 'POST', body: fd };
        } else {
          options = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ subject: this.newTicketSubject, body: this.newTicketBody }),
          };
        }
        const response = await this.apiFetch(`${this.apiUrl('tickets')}`, options);
        const data = await response.json();
        if (response.ok) {
          this._revokePicked(this.newTicketFiles);
          this.newTicketFiles = [];
          this.closeNewTicketDialog();
          this.showToast(data.message || 'Ticket created');
          await this.loadTickets();
          // Re-render explicitly so the list swaps in (empty state -> table for
          // the first ticket) without waiting on the async-handler auto-update.
          this.update();
        } else {
          this.showToast(data, 'danger');
        }
      } catch (e) {
        this.showToast(e.message, 'danger');
      } finally {
        this.newTicketLoading = false;
      }
    },

};
